import React, { type FC } from 'react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, formatDateRange } from './types';
import { normalizeAvatarUrl } from '../../../lib/avatar-url';

const defaultAvatar = require('../../../../assets/default-avatar.png');

interface Experience {
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string;
  current?: boolean;
}

export const CandidateDetailCard: FC<ToolCardProps> = ({ result }) => {
  // candidateView returns { profile: {...}, experiences: [...], cvs: [...] }
  // Also handle flat structure (candidateSearch returns flat profile objects)
  const profile = safeGet<Record<string, unknown>>(result, 'profile');
  const dataSource = profile ?? (result as Record<string, unknown>);

  const name = safeGet<string>(dataSource, 'name') ?? 'Unknown';
  const title = safeGet<string>(dataSource, 'title');
  const bio = safeGet<string>(dataSource, 'bio') ?? safeGet<string>(dataSource, 'about');
  const location = safeGet<string>(dataSource, 'location');
  const avatarUrl = safeGet<string>(dataSource, 'avatarUrl');
  const skills = (safeGet<string[]>(dataSource, 'skills') ?? []).slice(0, 5);

  // Experiences can be at result.experiences (candidateView) or result.profile has none
  const experiences = safeArray<Experience>(result, 'experiences').slice(-3);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={avatarUrl ? (normalizeAvatarUrl(avatarUrl, 80) ?? avatarUrl) : defaultAvatar}
            alt={name}
            referrerPolicy="no-referrer"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
              background: 'rgba(0,0,0,0.04)',
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </div>
            {(title || location) && (
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {title}
                {title && location && <span style={{ margin: '0 4px', opacity: 0.5 }}>&middot;</span>}
                {location}
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {bio && (
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: '20px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {bio}
          </div>
        )}

        {/* Experience */}
        {experiences.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
              Experience
            </div>
            {experiences.map((exp, i) => {
              const dateRange = formatDateRange(exp.startDate, exp.endDate, exp.current);
              return (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: '18px' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{exp.title || 'Role'}</span>
                  {exp.company && <span> at {exp.company}</span>}
                  {dateRange && <span style={{ color: 'var(--text-tertiary)' }}> ({dateRange})</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {skills.map((skill, i) => (
              <span key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.04)', borderRadius: 9999, padding: '2px 8px', lineHeight: '16px', whiteSpace: 'nowrap' }}>
                {skill}
              </span>
            ))}
          </div>
        )}
      </div>
    </CardShell>
  );
};
